import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (user && user.token) {
      config.headers.Authorization = `Bearer ${user.token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (username, password) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    
    const response = await axios.post(`${API_URL}/auth/login`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  
  getCurrentUser: async (token) => {
    const response = await axios.get(`${API_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  create: (userData) => api.post('/users', userData),
  delete: (userId) => api.delete(`/users/${userId}`)
};

// Products API
export const productsAPI = {
  getCategories: () => api.get('/products/categories'),
  getByCategory: (categoryId) => api.get(`/products/category/${categoryId}`),
  getById: (productId) => api.get(`/products/${productId}`),
  create: (productData) => api.post('/products', productData),
  update: (productId, productData) => api.put(`/products/${productId}`, productData),
  delete: (productId) => api.delete(`/products/${productId}`),
  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/products/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  uploadCertification: (productId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/products/${productId}/certifications`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteCertification: (productId, certId) =>
    api.delete(`/products/${productId}/certifications/${certId}`)
};

// Projects API
export const projectsAPI = {
  getAll: () => api.get('/projects'),
  getById: (projectId) => api.get(`/projects/${projectId}`),
  getByCode: (projectCode) => api.get(`/projects/code/${projectCode}`),
  create: (projectData) => api.post('/projects', projectData),
  update: (projectId, projectData) => api.put(`/projects/${projectId}`, projectData),
  delete: (projectId) => api.delete(`/projects/${projectId}`),
  
  // Progress
  getProgress: (projectId) => api.get(`/projects/${projectId}/progress`),
  updateProgress: (projectId, status, notes) => 
    api.put(`/projects/${projectId}/progress/${status}`, null, { params: { notes } }),
  
  // Files
  getFiles: (projectId) => api.get(`/projects/${projectId}/files`),
  uploadFile: async (projectId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/projects/${projectId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  deleteFile: (projectId, fileId) => api.delete(`/projects/${projectId}/files/${fileId}`),
  acceptOffer: (projectId) => api.put(`/projects/${projectId}/accept-offer`),
  rejectOffer: (projectId, reason) => api.put(`/projects/${projectId}/reject-offer`, { reason }),
  addNote: (projectId, content) => api.post(`/projects/${projectId}/notes`, { content })
};

// Messages API
export const messagesAPI = {
  getByProject: (projectId) => api.get(`/messages/${projectId}`),
  send: (data) => api.post('/messages', data)
};

// Specs API
export const specsAPI = {
  getAll: () => api.get('/specs'),
  upload: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/specs', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  delete: (specId) => api.delete(`/specs/${specId}`)
};

// Contact API
export const contactAPI = {
  getAll: () => api.get('/contact'),
  send: (data) => api.post('/contact', data)
};

// Customers API
export const customersAPI = {
  getAll: () => api.get('/customers'),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  addCRM: (id, data) => api.post(`/customers/${id}/crm`, data),
  uploadFile: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/customers/${id}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteFile: (customerId, fileId) => api.delete(`/customers/${customerId}/files/${fileId}`)
};

export default api;
